import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { apiAdmin, setSuperAdminToken, getDisplayMessage } from '@/api/apiClient';
import { Plus, Search, ExternalLink, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const statusLabels = {
  pending: 'Aguardando liberação',
  active: 'Ativo',
  trial: 'Trial',
  awaiting_payment: 'Aguardando pagamento',
  suspended: 'Suspenso',
  cancelled: 'Cancelado',
};

const statusVariants = {
  pending: 'outline',
  active: 'default',
  trial: 'secondary',
  awaiting_payment: 'outline',
  suspended: 'destructive',
  cancelled: 'outline',
};

export default function AdminEmpresas() {
  const [empresas, setEmpresas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [filtros, setFiltros] = useState({ status: '', search: '' });
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [modalNova, setModalNova] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [confirmSuspender, setConfirmSuspender] = useState(null);
  const [confirmExcluir, setConfirmExcluir] = useState(null);
  const [confirmApagar, setConfirmApagar] = useState(null);
  const [excluindoId, setExcluindoId] = useState(null);
  const [apagandoId, setApagandoId] = useState(null);
  const [formNova, setFormNova] = useState({
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    email_principal: '',
    telefone: '',
    plano_id: '',
    status: 'trial',
    usuario_admin: { nome: '', email: '', senha: '' },
  });

  const carregarEmpresas = async () => {
    setLoading(true);
    setErro('');
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filtros.status && { status: filtros.status }),
        ...(filtros.search && { search: filtros.search }),
      };
      const res = await apiAdmin.get('/api/admin/empresas', params);
      setEmpresas(res.empresas || []);
      setPagination((p) => ({ ...p, ...res.pagination }));
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      setErro(err?.message || 'Erro ao carregar empresas');
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarEmpresas();
  }, [pagination.page, filtros.status]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (filtros.search !== undefined) carregarEmpresas();
    }, 400);
    return () => clearTimeout(t);
  }, [filtros.search]);

  useEffect(() => {
    if (modalNova) {
      apiAdmin.get('/api/admin/planos').then((r) => setPlanos(Array.isArray(r) ? r : [])).catch(() => setPlanos([]));
    }
  }, [modalNova]);

  const abrirNovaEmpresa = () => {
    setFormNova({
      razao_social: '',
      nome_fantasia: '',
      cnpj: '',
      email_principal: '',
      telefone: '',
      plano_id: planos[0]?.id || '',
      status: 'trial',
      usuario_admin: { nome: '', email: '', senha: '' },
    });
    setModalNova(true);
  };

  const salvarNovaEmpresa = async () => {
    if (!formNova.razao_social?.trim() || !formNova.cnpj?.trim() || !formNova.email_principal?.trim()) {
      toast.error('Preencha razão social, CNPJ e e-mail.');
      return;
    }
    if (!formNova.plano_id) {
      toast.error('Selecione um plano.');
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        razao_social: formNova.razao_social.trim(),
        nome_fantasia: formNova.nome_fantasia?.trim() || undefined,
        cnpj: formNova.cnpj.replace(/\D/g, ''),
        email_principal: formNova.email_principal.trim(),
        telefone: formNova.telefone?.trim() || undefined,
        plano_id: formNova.plano_id,
        status: formNova.status,
      };
      if (formNova.usuario_admin?.nome && formNova.usuario_admin?.email && formNova.usuario_admin?.senha) {
        payload.usuario_admin = formNova.usuario_admin;
      }
      await apiAdmin.post('/api/admin/empresas', payload);
      setModalNova(false);
      carregarEmpresas();
      toast.success('Empresa criada com sucesso.');
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao criar empresa. Tente novamente.'));
    } finally {
      setSalvando(false);
    }
  };

  const suspenderEmpresa = async (id) => {
    try {
      await apiAdmin.post('/api/admin/empresas/' + id + '/suspender', { motivo: 'Suspensão administrativa' });
      carregarEmpresas();
      setConfirmSuspender(null);
      toast.success('Empresa suspensa.');
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao suspender empresa. Tente novamente.'));
    }
  };

  const ativarEmpresa = async (id) => {
    try {
      await apiAdmin.post('/api/admin/empresas/' + id + '/ativar');
      carregarEmpresas();
      toast.success('Empresa ativada.');
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao ativar empresa. Tente novamente.'));
    }
  };

  const liberarCadastro = async (id) => {
    try {
      await apiAdmin.post('/api/admin/empresas/' + id + '/liberar-cadastro');
      carregarEmpresas();
      toast.success('Cadastro liberado. Trial de 14 dias iniciado; e-mail enviado ao administrador.');
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao liberar cadastro.'));
    }
  };

  const executarExclusaoEmpresa = async (id) => {
    setExcluindoId(id);
    try {
      await apiAdmin.delete('/api/admin/empresas/' + id);
      setConfirmExcluir(null);
      carregarEmpresas();
      toast.success('Empresa excluída (cadastro desativado; dados mantidos para auditoria).');
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      toast.error(getDisplayMessage(err, 'Erro ao excluir empresa.'));
    } finally {
      setExcluindoId(null);
    }
  };

  const executarApagarEmpresa = async (id) => {
    setApagandoId(id);
    try {
      await apiAdmin.delete('/api/admin/empresas/' + id + '/apagar-definitivo');
      setConfirmApagar(null);
      carregarEmpresas();
      toast.success('Empresa apagada definitivamente. Todos os dados foram removidos.');
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      toast.error(getDisplayMessage(err, 'Erro ao apagar empresa definitivamente.'));
    } finally {
      setApagandoId(null);
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

  return (
    <div className="p-6 w-full max-w-[min(100%,92rem)] mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestão de Empresas</h1>
          <p className="text-slate-600">Gerencie as empresas da plataforma</p>
        </div>
        <Button onClick={abrirNovaEmpresa}>
          <Plus className="h-4 w-4" />
          Nova empresa
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por CNPJ, razão social ou e-mail..."
                value={filtros.search || ''}
                onChange={(e) => setFiltros((f) => ({ ...f, search: e.target.value }))}
                className="pl-9"
              />
            </div>
            <Select
              value={filtros.status || 'all'}
              onValueChange={(v) => setFiltros((f) => ({ ...f, status: v === 'all' ? '' : v }))}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="pending">Aguardando liberação</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="awaiting_payment">Aguardando pagamento</SelectItem>
                <SelectItem value="active">Ativo</SelectItem>
                <SelectItem value="suspended">Suspenso</SelectItem>
                <SelectItem value="cancelled">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {erro && (
            <p className="text-destructive text-sm mb-4">{erro}</p>
          )}

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table className="min-w-[960px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Razão Social</TableHead>
                  <TableHead>CNPJ</TableHead>
                  <TableHead>Plano</TableHead>
                  <TableHead>Data Final</TableHead>
                  <TableHead>Data Adesão</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Usuários</TableHead>
                  <TableHead className="text-right w-[1%] whitespace-nowrap min-w-[28rem]">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {empresas.map((empresa) => (
                  <TableRow key={empresa.id}>
                    <TableCell className="font-medium">{empresa.razaoSocial}</TableCell>
                    <TableCell className="text-slate-600">{empresa.cnpj}</TableCell>
                    <TableCell className="text-slate-600">{empresa.plano?.nome ?? '-'}</TableCell>
                    <TableCell className="text-slate-600">{formatDate(empresa.dataExpiracao)}</TableCell>
                    <TableCell className="text-slate-600">
                      {formatDate(empresa.dataAtivacao || empresa.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariants[empresa.status] || 'outline'}>
                        {statusLabels[empresa.status] || empresa.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-600">{empresa._count?.usuarios ?? 0}</TableCell>
                    <TableCell className="text-right align-middle p-2">
                      <div className="inline-flex flex-nowrap items-center justify-end gap-1.5 max-w-none">
                        <Button asChild variant="ghost" size="sm" className="h-8 shrink-0 px-2">
                          <Link to={'/admin/empresas/' + empresa.id} className="flex items-center gap-1">
                            <ExternalLink className="h-4 w-4 shrink-0" />
                            <span>Detalhes</span>
                          </Link>
                        </Button>
                        {empresa.status === 'pending' && (
                          <Button
                            variant="default"
                            size="sm"
                            className="h-8 shrink-0 bg-emerald-600 hover:bg-emerald-700 px-2"
                            onClick={() => liberarCadastro(empresa.id)}
                          >
                            Liberar acesso
                          </Button>
                        )}
                        {empresa.status === 'suspended' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 shrink-0 px-2 text-green-600 border-green-200 hover:bg-green-50"
                            onClick={() => ativarEmpresa(empresa.id)}
                          >
                            Ativar
                          </Button>
                        ) : empresa.status !== 'pending' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 shrink-0 px-2 text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => setConfirmSuspender(empresa)}
                          >
                            Suspender
                          </Button>
                        ) : null}
                        {!empresa.deletedAt && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 shrink-0 px-2 text-destructive border-destructive/30 hover:bg-destructive/10"
                            onClick={() => setConfirmExcluir(empresa)}
                            title="Exclusão lógica (auditoria)"
                          >
                            <Trash2 className="h-4 w-4 shrink-0" />
                            <span className="ml-1">Excluir</span>
                          </Button>
                        )}
                        {!empresa.deletedAt && (
                          <Button
                            variant="destructive"
                            size="sm"
                            className="h-8 shrink-0 px-2"
                            onClick={() => setConfirmApagar(empresa)}
                            title="Remove todos os dados da empresa"
                          >
                            <Trash2 className="h-4 w-4 shrink-0" />
                            <span className="ml-1 hidden lg:inline">Apagar empresa</span>
                            <span className="ml-1 lg:hidden">Apagar</span>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {pagination.pages > 1 && !loading && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
              >
                Anterior
              </Button>
              <span className="text-sm text-slate-600">
                Página {pagination.page} de {pagination.pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.pages}
                onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              >
                Próxima
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={modalNova} onOpenChange={setModalNova}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova empresa</DialogTitle>
            <DialogDescription>Preencha os dados para criar uma nova empresa na plataforma.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="razao_social">Razão social *</Label>
              <Input
                id="razao_social"
                value={formNova.razao_social}
                onChange={(e) => setFormNova((f) => ({ ...f, razao_social: e.target.value }))}
                placeholder="Razão social"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nome_fantasia">Nome fantasia</Label>
              <Input
                id="nome_fantasia"
                value={formNova.nome_fantasia}
                onChange={(e) => setFormNova((f) => ({ ...f, nome_fantasia: e.target.value }))}
                placeholder="Nome fantasia"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cnpj">CNPJ *</Label>
              <Input
                id="cnpj"
                value={formNova.cnpj}
                onChange={(e) => setFormNova((f) => ({ ...f, cnpj: e.target.value }))}
                placeholder="00.000.000/0001-00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email_principal">E-mail principal *</Label>
              <Input
                id="email_principal"
                type="email"
                value={formNova.email_principal}
                onChange={(e) => setFormNova((f) => ({ ...f, email_principal: e.target.value }))}
                placeholder="contato@empresa.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="telefone">Telefone</Label>
              <Input
                id="telefone"
                value={formNova.telefone}
                onChange={(e) => setFormNova((f) => ({ ...f, telefone: e.target.value }))}
                placeholder="(00) 00000-0000"
              />
            </div>
            <div className="space-y-2">
              <Label>Plano *</Label>
              <Select
                value={formNova.plano_id}
                onValueChange={(v) => setFormNova((f) => ({ ...f, plano_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {planos.filter((p) => p.ativo).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status inicial</Label>
              <Select
                value={formNova.status}
                onValueChange={(v) => setFormNova((f) => ({ ...f, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Ativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 border-t pt-4">
              <Label>Usuário admin (opcional)</Label>
              <div className="space-y-2">
                <Input
                  value={formNova.usuario_admin?.nome || ''}
                  onChange={(e) => setFormNova((f) => ({ ...f, usuario_admin: { ...f.usuario_admin, nome: e.target.value } }))}
                  placeholder="Nome do admin"
                />
                <Input
                  type="email"
                  value={formNova.usuario_admin?.email || ''}
                  onChange={(e) => setFormNova((f) => ({ ...f, usuario_admin: { ...f.usuario_admin, email: e.target.value } }))}
                  placeholder="E-mail do admin"
                />
                <Input
                  type="password"
                  value={formNova.usuario_admin?.senha || ''}
                  onChange={(e) => setFormNova((f) => ({ ...f, usuario_admin: { ...f.usuario_admin, senha: e.target.value } }))}
                  placeholder="Senha do admin"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalNova(false)}>
              Cancelar
            </Button>
            <Button onClick={salvarNovaEmpresa} disabled={salvando}>
              {salvando ? 'Salvando...' : 'Incluir empresa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmSuspender} onOpenChange={(open) => !open && setConfirmSuspender(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar suspensão</AlertDialogTitle>
            <AlertDialogDescription>
              Deseja realmente suspender a empresa {confirmSuspender?.razaoSocial}? Esta ação pode ser revertida posteriormente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => confirmSuspender && suspenderEmpresa(confirmSuspender.id)}
            >
              Suspender
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmExcluir} onOpenChange={(open) => !open && setConfirmExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir empresa</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  Confirma a exclusão de <strong className="text-foreground">{confirmExcluir?.razaoSocial}</strong>?
                </p>
                <p>
                  A empresa será desativada (exclusão lógica): status cancelado, data de exclusão registrada e o acesso
                  dos usuários deixa de ser válido. Os dados permanecem no banco para auditoria.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!excluindoId}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={!!excluindoId}
              onClick={(e) => {
                e.preventDefault();
                if (confirmExcluir) executarExclusaoEmpresa(confirmExcluir.id);
              }}
            >
              {excluindoId ? 'Excluindo...' : 'Excluir definitivamente'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmApagar} onOpenChange={(open) => !open && setConfirmApagar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apagar empresa definitivamente</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  Confirma o apagamento definitivo de <strong className="text-foreground">{confirmApagar?.razaoSocial}</strong>?
                </p>
                <p>
                  Esta ação é irreversível e remove todos os dados da empresa no sistema. Nada será mantido para auditoria.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!apagandoId}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={!!apagandoId}
              onClick={(e) => {
                e.preventDefault();
                if (confirmApagar) executarApagarEmpresa(confirmApagar.id);
              }}
            >
              {apagandoId ? 'Apagando...' : 'Apagar definitivamente'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
